import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import { AlertState, FinanceiroMensal } from '../types';
import './PlanilhaMensal.css';

interface MensalFormData {
  cogs: string;
  vendasMarketing: string;
  saldoCaixa: string;
  netCashBurn: string;
}

const createEmptyForm = (): MensalFormData => ({
  cogs: '',
  vendasMarketing: '',
  saldoCaixa: '',
  netCashBurn: '',
});

const centsToForm = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');

const formToCents = (valor: string): number => {
  const normalized = valor.replace(/\s|R\$/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = Number(normalized);
  if (!normalized || Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
};

const formatCents = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatCompetencia = (competencia: string): string => {
  const [ano, mes] = competencia.split('-');
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  return data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Sugere a próxima competência a lançar (mês seguinte ao mais recente, ou o mês atual). */
const proximaCompetencia = (meses: FinanceiroMensal[]): string => {
  if (meses.length === 0) {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
  }
  const [ano, mes] = meses[0].competencia.split('-').map(Number);
  const proximo = new Date(ano, mes, 1);
  return `${proximo.getFullYear()}-${String(proximo.getMonth() + 1).padStart(2, '0')}`;
};

const PlanilhaMensal: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [meses, setMeses] = useState<FinanceiroMensal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCompetencia, setEditingCompetencia] = useState<string>('');
  const [formData, setFormData] = useState<MensalFormData>(createEmptyForm());
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [ressyncingId, setRessyncingId] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });

  const canManage = user?.role === 'admin' || user?.role === 'manager';

  const loadMeses = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<FinanceiroMensal[]>('/financeiro-mensal');
      setMeses(response.data);
    } catch (error) {
      console.error('Erro ao carregar planilha mensal:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMeses();
  }, [loadMeses]);

  const openNewMonth = (): void => {
    setEditingCompetencia(proximaCompetencia(meses));
    setFormData(createEmptyForm());
    setShowForm(true);
  };

  const openEditMonth = (mes: FinanceiroMensal): void => {
    setEditingCompetencia(mes.competencia.slice(0, 7));
    setFormData({
      cogs: centsToForm(mes.cogsCents),
      vendasMarketing: centsToForm(mes.vendasMarketingCents),
      saldoCaixa: centsToForm(mes.saldoCaixaCents),
      netCashBurn: centsToForm(mes.netCashBurnCents),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!editingCompetencia) {
      setAlert({ isOpen: true, message: 'Informe a competência (mês/ano).', type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/financeiro-mensal/${editingCompetencia}`, {
        cogsCents: formToCents(formData.cogs),
        vendasMarketingCents: formToCents(formData.vendasMarketing),
        saldoCaixaCents: formToCents(formData.saldoCaixa),
        netCashBurnCents: formToCents(formData.netCashBurn),
      });
      setShowForm(false);
      await loadMeses();
      setAlert({ isOpen: true, message: 'Mês salvo e sincronizado com o board.', type: 'success' });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao salvar o mês',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRessincronizar = async (mes: FinanceiroMensal): Promise<void> => {
    setRessyncingId(mes.id);
    try {
      const competencia = mes.competencia.slice(0, 7);
      const response = await api.post<FinanceiroMensal>(
        `/financeiro-mensal/${competencia}/ressincronizar`
      );
      setMeses((prev) => prev.map((m) => (m.id === mes.id ? response.data : m)));
      if (response.data.syncErro) {
        setAlert({ isOpen: true, message: `Falha ao sincronizar: ${response.data.syncErro}`, type: 'error' });
      } else {
        setAlert({ isOpen: true, message: 'Sincronizado com o board.', type: 'success' });
      }
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao ressincronizar',
        type: 'error',
      });
    } finally {
      setRessyncingId(null);
    }
  };

  const stats = useMemo(() => {
    const pendentes = meses.filter((m) => m.syncErro).length;
    const ultima = meses[0];
    return { pendentes, ultima };
  }, [meses]);

  if (loading) {
    return (
      <div className="mensal-state">
        <div className="mensal-state-card">
          <span className="mensal-badge">Carregando</span>
          <h1>Preparando planilha mensal</h1>
          <p>Buscando os meses lançados e o status de sincronização com o board.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mensal-page">
      <section className="mensal-hero">
        <div className="mensal-hero-copy">
          <span className="mensal-badge">Planilha mensal</span>
          <h1>Fechamento financeiro do mês</h1>
          <p>
            Lance COGS, vendas &amp; marketing, saldo de caixa e net cash burn de cada mês. Ao
            salvar, os números são enviados automaticamente ao dashboard do board.
          </p>
        </div>
        <div className="mensal-hero-side">
          <div className="mensal-hero-card">
            <span className="mensal-hero-card-label">Status</span>
            <strong>
              {meses.length} mês(es) lançado(s)
              {stats.pendentes > 0 ? ` · ${stats.pendentes} com falha de sync` : ''}
            </strong>
            <p>
              {stats.ultima
                ? `Último mês: ${formatCompetencia(stats.ultima.competencia.slice(0, 7))}`
                : 'Nenhum mês lançado ainda.'}
            </p>
          </div>
          {canManage && (
            <button type="button" className="mensal-action-button" onClick={openNewMonth}>
              Lançar mês
            </button>
          )}
        </div>
      </section>

      {showForm && canManage && (
        <section className="mensal-surface">
          <div className="mensal-section-header">
            <div>
              <span className="mensal-section-kicker">Cadastro</span>
              <h2>{formatCompetencia(editingCompetencia || proximaCompetencia(meses))}</h2>
            </div>
            <p>Valores em reais (R$). Ao salvar, sincroniza automaticamente com o board.</p>
          </div>

          <form className="mensal-form" onSubmit={handleSubmit}>
            <div className="mensal-form-grid">
              <div className="form-group">
                <label htmlFor="mensal-competencia">Competência (mês/ano) *</label>
                <input
                  id="mensal-competencia"
                  className="premium-text-input"
                  type="month"
                  value={editingCompetencia}
                  onChange={(e) => setEditingCompetencia(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="mensal-cogs">COGS (R$)</label>
                <input
                  id="mensal-cogs"
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.cogs}
                  onChange={(e) => setFormData({ ...formData, cogs: e.target.value })}
                  placeholder="Ex.: 12500,00"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mensal-vendas-marketing">Vendas &amp; Marketing (R$)</label>
                <input
                  id="mensal-vendas-marketing"
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.vendasMarketing}
                  onChange={(e) => setFormData({ ...formData, vendasMarketing: e.target.value })}
                  placeholder="Ex.: 8000,00"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mensal-saldo-caixa">Saldo de caixa (R$)</label>
                <input
                  id="mensal-saldo-caixa"
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.saldoCaixa}
                  onChange={(e) => setFormData({ ...formData, saldoCaixa: e.target.value })}
                  placeholder="Ex.: 150000,00"
                />
              </div>
              <div className="form-group">
                <label htmlFor="mensal-net-cash-burn">Net cash burn (R$)</label>
                <input
                  id="mensal-net-cash-burn"
                  className="premium-text-input"
                  type="text"
                  inputMode="decimal"
                  value={formData.netCashBurn}
                  onChange={(e) => setFormData({ ...formData, netCashBurn: e.target.value })}
                  placeholder="Negativo se o caixa cresceu no mês"
                />
              </div>
            </div>

            <div className="mensal-form-footer">
              <button
                type="button"
                className="mensal-form-btn is-ghost"
                onClick={() => setShowForm(false)}
              >
                Cancelar
              </button>
              <button type="submit" className="mensal-form-btn is-primary" disabled={submitting}>
                {submitting ? 'Salvando…' : 'Salvar e sincronizar'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="mensal-surface">
        <div className="mensal-section-header">
          <div>
            <span className="mensal-section-kicker">Histórico</span>
            <h2>Meses lançados</h2>
          </div>
          <p>{meses.length === 0 ? 'Nenhum mês lançado ainda.' : `${meses.length} mês(es) no total`}</p>
        </div>

        {meses.length === 0 ? (
          <div className="mensal-empty-block">
            <strong>Nada por aqui</strong>
            <p>
              {canManage
                ? 'Lance o primeiro mês para começar a alimentar o dashboard do board.'
                : 'Nenhum mês lançado para a sua organização.'}
            </p>
          </div>
        ) : (
          <div className="mensal-table-wrap">
            <table className="mensal-table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th>COGS</th>
                  <th>Vendas &amp; Marketing</th>
                  <th>Saldo de caixa</th>
                  <th>Net cash burn</th>
                  <th>Sincronização</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {meses.map((mes) => (
                  <tr key={mes.id}>
                    <td className="mensal-table-month">{formatCompetencia(mes.competencia.slice(0, 7))}</td>
                    <td>{formatCents(mes.cogsCents)}</td>
                    <td>{formatCents(mes.vendasMarketingCents)}</td>
                    <td>{formatCents(mes.saldoCaixaCents)}</td>
                    <td className={mes.netCashBurnCents < 0 ? 'mensal-burn-negative' : undefined}>
                      {formatCents(mes.netCashBurnCents)}
                    </td>
                    <td>
                      {mes.syncErro ? (
                        <span className="mensal-sync-badge is-erro" title={mes.syncErro}>
                          Falha no sync
                        </span>
                      ) : mes.sincronizadoEm ? (
                        <span className="mensal-sync-badge is-ok">
                          Sincronizado · {formatDateTime(mes.sincronizadoEm)}
                        </span>
                      ) : (
                        <span className="mensal-sync-badge is-pendente">Nunca sincronizado</span>
                      )}
                    </td>
                    <td className="mensal-table-actions">
                      {canManage && (
                        <>
                          <button
                            type="button"
                            className="mensal-table-button"
                            onClick={() => openEditMonth(mes)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="mensal-table-button"
                            disabled={ressyncingId === mes.id}
                            onClick={() => handleRessincronizar(mes)}
                          >
                            {ressyncingId === mes.id ? 'Sincronizando…' : 'Ressincronizar'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default PlanilhaMensal;
